import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const AuthContext = createContext();
const API_BASE = process.env.REACT_APP_USER_SERVICE_URL;

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [auth, setAuth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);

  // ------------------- Decode JWT -------------------
  const decodeJWT = (token) => {
    try {
      const payload = token.split(".")[1];
      return JSON.parse(atob(payload));
    } catch (err) {
      console.error("Invalid JWT:", err);
      return null;
    }
  };

  // ------------------- Init from localStorage -------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      const decoded = decodeJWT(token);

      if (decoded && decoded.exp * 1000 > Date.now()) {
        setAuth({
          userId: decoded.userId,
          email: decoded.sub,
          phone: decoded.phone || "",
          firstName: decoded.fullName?.split(" ")[0] || "",
          token,
          roles: decoded.role ? [decoded.role] : [],
        });
      } else {
        localStorage.removeItem("authToken");
      }
    }
    setLoading(false);
  }, []);

  // ------------------- Login -------------------
  const login = (authData) => {
    if (!authData.token) throw new Error("Missing token in login data");

    const decoded = decodeJWT(authData.token);
    if (!decoded) throw new Error("Invalid JWT token");

    const roles = decoded.role ? [decoded.role] : [];

    const user = {
      userId: decoded.userId,
      email: decoded.sub,
      phone: decoded.phone || "",
      firstName: decoded.fullName?.split(" ")[0] || "",
      token: authData.token,
      roles,
    };

    // Save in localStorage
    localStorage.setItem("authToken", authData.token);
    localStorage.setItem("authUserId", user.userId);
    localStorage.setItem("authEmail", user.email);
    localStorage.setItem("authPhone", user.phone);
    localStorage.setItem("authFirstName", user.firstName);
    localStorage.setItem("authRoles", roles.join(","));

    setAuth(user);
  };

  // ------------------- Logout -------------------
  const logout = async (callBackend = true) => {
    if (loggingOut) return;
    setLoggingOut(true);

    const token = localStorage.getItem("authToken");
    let backendMessage = "";

    if (callBackend && token) {
      try {
        const res = await fetch(`${API_BASE}/users/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          throw new Error("Server logout faild");
        }
        const data = await res.json();
        backendMessage = data?.message || "";
      } catch {
        backendMessage =
          "Logout failed on server, but you are logged out on frontend.";
      }
    }

    // Clear localStorage
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUserId");
    localStorage.removeItem("authEmail");
    localStorage.removeItem("authPhone");
    localStorage.removeItem("authFirstName");
    localStorage.removeItem("authRoles");

    setAuth(null);
    setLoggingOut(false);
    console.log(backendMessage);

    navigate("/login", { replace: true });
  };

  // ------------------- Auth Fetch -------------------
  const authFetch = async (endpoint, options = {}) => {
    const token = localStorage.getItem("authToken");

    const headers = {
      ...(options.headers || {}),
      Authorization: token ? `Bearer ${token}` : "",
    };

    // only set JSON if body is not form data
    if (!(options.body instanceof FormData)) {
      headers["Content-type"] = "application/json";
    }

    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (res.status === 401) {
      await logout(false);
      throw new Error("Unauthorized");
    }

    return res;
  };

  const isAuthenticated = !!auth?.token;

  return (
    <AuthContext.Provider
      value={{
        auth,
        isAuthenticated,
        login,
        logout,
        authFetch,
        loading,
        loggingOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
